







































































































import { Component, Vue } from "vue-property-decorator";
import { rootModule } from "@/store/root";
import {
  formatCompiler,
  FormatComponent,
  parseFormat,
  PropComponent
} from "@/util/string";
import { deepAssign } from "@/util/deep";
import { id } from "@/util/hash";

@Component
export default class App extends Vue {
  showCopiedSnackbar = false;

  aliasComponentData: Record<string, { text: string; value: string }> = {};

  get aliasBlueprint(): string {
    return rootModule.aliasBlueprint;
  }

  set aliasBlueprint(newAliasBlueprint: string) {
    rootModule.setAliasBlueprint(newAliasBlueprint);
  }

  get aliasComponents() {
    return parseFormat(this.aliasBlueprint);
  }

  get aliasCompiler() {
    return formatCompiler(this.aliasComponents);
  }

  get aliasPreview(): string {
    return `${this.address}+${this.aliasCompiler(
      this.aliasPreviewBinding
    )}+1@gmail.com`;
  }

  get aliasComponentObject() {
    return Object.fromEntries(
      this.aliasComponents
        .filter(
          component =>
            component.type === "prop" && component.prop !== "timestamp"
        )
        .map(component => [(component as PropComponent).prop, ""])
    );
  }

  get aliasBinding() {
    return deepAssign(
      this.aliasPreviewBinding,
      this.aliasComponentObject,
      Object.fromEntries(
        Object.entries(this.aliasComponentData).map(([key, component]) => [
          key,
          component.value
        ])
      )
    );
  }

  get compiledAliasComponent(): string {
    return `${this.address}+${this.aliasCompiler(this.aliasBinding)}`;
  }

  countForAlias(alias: string): number {
    return alias in rootModule.counts ? rootModule.counts[alias] : 0;
  }

  get aliasPreviewBinding() {
    return {
      ...Object.fromEntries(
        Object.keys(this.aliasComponentObject).map(key => [key, `{${key}}`])
      ),
      timestamp: this.timestamp
    };
  }

  get address() {
    return rootModule.address;
  }

  set address(newAddress: string) {
    rootModule.setAddress(newAddress);
  }

  addAlias() {
    const newAliases = Array.from(this.aliases);
    const newAlias = `${this.compiledAliasComponent}+${this.countForAlias(
      this.compiledAliasComponent
    ) + 1}@gmail.com`;
    rootModule.setCounts(
      Object.assign(rootModule.counts, {
        [this.compiledAliasComponent]:
          this.countForAlias(this.compiledAliasComponent) + 1
      })
    );
    newAliases.push(newAlias);
    this.aliases = newAliases;
  }

  get aliases() {
    return rootModule.aliases;
  }

  set aliases(newAliases) {
    rootModule.setAliases(newAliases);
  }

  removeAlias(aliasIndex: number) {
    const newAliases = Array.from(this.aliases);
    newAliases.splice(aliasIndex, 1);
    this.aliases = newAliases;
  }

  aliasPropComponentDataGetter(
    componentKey: string | number
  ): { text: string; value: string } {
    return this.aliasComponentData[componentKey];
  }

  aliasPropComponentDataSetter(componentKey: string | number) {
    return (newValue: string | { text: string; value: string }) => {
      let validatedValue: { text: string; value: string };
      if (typeof newValue === typeof "") {
        validatedValue = { text: String(newValue), value: String(newValue) };
        rootModule.addAliasPropComponent({
          componentKey,
          item: String(newValue)
        });
      } else {
        validatedValue = newValue as { text: string; value: string };
      }
      this.$set(this.aliasComponentData, componentKey, validatedValue);
    };
  }

  aliasPropComponentOptions(
    componentKey: string | number
  ): { text: string; value: string }[] {
    return componentKey in rootModule.aliasPropComponents
      ? rootModule.aliasPropComponents[componentKey].map(component => ({
          text: component,
          value: component
        }))
      : [];
  }

  get timestamp(): string {
    const now = new Date();
    const year = this.zeroPad(now.getFullYear(), 4);
    const month = this.zeroPad(now.getMonth() + 1, 2);
    const date = this.zeroPad(now.getDate(), 2);
    return `${year}${month}${date}`;
  }

  getId(obj: FormatComponent): string {
    return id(obj);
  }

  zeroPad(num: number, padLength: number): string {
    const pad = new Array(padLength).fill(0).join("");
    return `${pad}${num}`.slice(-padLength);
  }

  copyToClipboard(text: string) {
    const pasteBoard = document.createElement("input");
    document.body.appendChild(pasteBoard);
    pasteBoard.value = text;
    pasteBoard.select();
    document.execCommand("copy");
    document.body.removeChild(pasteBoard);
    this.showCopiedSnackbar = true;
  }
}
